/** Часть поля времени записи */
export type TimePart = 'hours' | 'minutes' | 'seconds' | 'milliseconds';

/** Набранное в частях поля времени: цифры каждой части, пустая строка — часть не заполнена */
export type TimeParts = Record<TimePart, string>;

/** Час записи, мс: запись от часа и длиннее показывается и вводится с часами */
export const HOUR_MS = 3_600_000;

/** Число цифр в части: миллисекунд три, остальных частей — две */
export const TIME_PART_LENGTH: Record<TimePart, number> = { hours: 2, minutes: 2, seconds: 2, milliseconds: 3 };

/** Наибольшее значение части; у часов предела нет — их ограничивает число цифр */
const TIME_PART_MAX: Partial<Record<TimePart, number>> = { minutes: 59, seconds: 59 };

const EMPTY_TIME_PARTS: TimeParts = { hours: '', minutes: '', seconds: '', milliseconds: '' };

/** Части поля в порядке ввода */
export const getTimePartOrder = (hasHours: boolean): TimePart[] =>
  hasHours ? ['hours', 'minutes', 'seconds', 'milliseconds'] : ['minutes', 'seconds', 'milliseconds'];

/**
 * Набранные цифры части: только цифры и не больше длины части. Первая цифра минут и секунд больше пяти
 * дополняется ведущим нулём — вторую к ней уже не набрать, — а значение сверх предела части прижимается к нему.
 */
export const readTimePartInput = (part: TimePart, input: string): string => {
  let digits = input.replace(/\D/g, '').slice(0, TIME_PART_LENGTH[part]);
  const max = TIME_PART_MAX[part];
  if (max !== undefined && digits.length === 1 && Number(digits) > Math.floor(max / 10)) digits = `0${digits}`;
  if (max !== undefined && Number(digits) > max) digits = String(max);
  return digits;
};

/**
 * Часть, дописанная до полной длины: часы, минуты и секунды — ведущими нулями, миллисекунды — нулями
 * справа, как доли секунды после точки (`5` — 500 мс). Значение части от этого не меняется.
 */
export const padTimePart = (part: TimePart, digits: string): string => {
  if (!digits) return digits;
  const length = TIME_PART_LENGTH[part];
  return part === 'milliseconds' ? digits.padEnd(length, '0') : digits.padStart(length, '0');
};

/** Части поля по времени в миллисекундах; без часов минуты вмещают всё время */
export const splitTimeMs = (ms: number | null, hasHours: boolean): TimeParts => {
  if (ms === null) return EMPTY_TIME_PARTS;
  const totalSeconds = Math.floor(ms / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  return {
    hours: hasHours ? padTimePart('hours', String(Math.floor(totalMinutes / 60))) : '',
    minutes: padTimePart('minutes', String(hasHours ? totalMinutes % 60 : totalMinutes)),
    seconds: padTimePart('seconds', String(totalSeconds % 60)),
    milliseconds: String(ms % 1000).padStart(TIME_PART_LENGTH.milliseconds, '0'),
  };
};

/** Время в миллисекундах по частям поля; null — заполнены не все части */
export const joinTimeParts = (parts: TimeParts, order: TimePart[]): number | null => {
  if (order.some((part) => parts[part] === '')) return null;
  const hours = order.includes('hours') ? Number(parts.hours) : 0;
  const milliseconds = Number(padTimePart('milliseconds', parts.milliseconds));
  return ((hours * 60 + Number(parts.minutes)) * 60 + Number(parts.seconds)) * 1000 + milliseconds;
};
